import { useState } from 'react';
import { Button } from 'src/components/ui/button';
import { T } from 'gt-react';
import useAuth from 'src/api/auth/useAuth';
import { selectRun } from '../agentPageRuns';
import type { AgentPageRuns } from '../useAgentPageRuns';
import { AgentRunDetailCard } from './AgentRunDetailCard';
import { AgentRunHistory } from './AgentRunHistory';

/** The run history beside the selected run's details. */
export function AgentRunsSection({
  history,
  agentDefinitionId,
  agentName,
}: {
  history: AgentPageRuns['history'];
  agentDefinitionId: string;
  agentName: string;
}) {
  const { runs } = history;
  const { currentUser } = useAuth();
  // The user's pick sticks; before they pick, follow the live/newest run so a
  // freshly launched run opens itself.
  const [pickedRunId, setPickedRunId] = useState<string | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const selectedRun = selectRun(runs, pickedRunId);

  return (
    <div
      className={
        inspecting
          ? 'grid min-w-0 gap-3'
          : 'grid items-start gap-4 lg:grid-cols-[minmax(260px,1fr)_minmax(0,2fr)]'
      }
    >
      {inspecting && (
        <Button
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={() => setHistoryOpen(!historyOpen)}
          aria-expanded={historyOpen}
        >
          <T context="agent run inspector history toggle">History</T>
        </Button>
      )}
      <div hidden={inspecting && !historyOpen}>
        <AgentRunHistory
          runs={runs}
          total={history.total}
          hiddenCount={history.hiddenCount}
          filter={history.filter}
          onFilterChange={history.onFilterChange}
          selectedRunId={selectedRun?.id ?? null}
          onSelectRun={(id) => {
            setPickedRunId(id);
            setInspecting(false);
            setHistoryOpen(false);
          }}
          onLoadMore={history.onLoadMore}
          isLoadingMore={history.isFetching}
        />
      </div>
      {selectedRun ? (
        <AgentRunDetailCard
          key={selectedRun.id}
          run={selectedRun}
          agentDefinitionId={agentDefinitionId}
          agentName={agentName}
          onInspectionChange={setInspecting}
          organizationId={currentUser?.organizationId}
        />
      ) : (
        <section className="flex items-center justify-center rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
          <T context="agent page placeholder beside an empty run history">
            Run details appear here once the agent has run.
          </T>
        </section>
      )}
    </div>
  );
}
